import s from './Telegram.module.sass';

import React, { FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  Header,
  List,
  ListItem,
  LottieIcon,
  Placeholder,
  ScrollView,
} from '$uikit';
import { telegramActions, telegramSelector } from '$store/telegram';
import { Loader } from '$shared/components';
import { useTranslator } from '$hooks';
import { mainActions } from '$store/main';
import { ConnectBotSheet } from '$core/Bot/ConnectBotSheet/ConnectBotSheet';
import { IcCopy28, IcPlusCircle28, IcSettings28 } from '$assets';
import { BOT } from '$shared/constants/pages';
import router from '$navigation/router';
import { imActions } from '$store/im';
import { showModal } from '$navigation/helper';
import copyToClipboard from 'copy-text-to-clipboard';
import { toastActions } from '$store/toast';

export const Telegram: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const { loadingStatus, botConnected, connectedChannel, inviteLink } =
    useSelector(telegramSelector);

  const loadData = () => {
    dispatch(telegramActions.loadDashboard());
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleConnectBot = () => {
    dispatch(mainActions.setPopup(<ConnectBotSheet />));
  };

  const handleCreatePost = useCallback(() => {
    dispatch(imActions.setTelegramSheetVisible(false));
    showModal('create_invoice', false, {
      peer_id: 0,
      type: 'tg',
    });
  }, []);

  const handleSelectChannel = () => {
    dispatch(imActions.showChooseChatTelegramSheetWithType('connect'));
  };

  const handleCopyLink = () => {
    copyToClipboard(inviteLink);
    dispatch(toastActions.setToastSuccess(t('link_copied')));
  };

  function renderContent() {
    if (loadingStatus === 'loading') {
      return <Loader isWrapped />;
    }

    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
          centered
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (!botConnected) {
      return (
        <ScrollView
          footer={<Button onClick={handleConnectBot}>{t('telegram_button')}</Button>}
        >
          <Placeholder
            iconSize={112}
            icon={<LottieIcon name="duck_money_mouth_face" loop />}
            title={t('telegram_title')}
            compact
          >
            {t('telegram_caption')}
          </Placeholder>
          <div className={s.features}>
            <div className={s.featureRow}>
              <div className={s.featureRowTitle}>
                {t('telegram_featured_private_channels_title')}
              </div>
              <div className={s.featureRowCaption}>
                {t('telegram_featured_private_channels_caption')}
              </div>
            </div>
            <div className={s.featureRow}>
              <div className={s.featureRowTitle}>
                {t('telegram_featured_paid_content_title')}
              </div>
              <div className={s.featureRowCaption}>
                {t('telegram_featured_paid_content_caption')}
              </div>
            </div>
            <div className={s.featureRow}>
              <div className={s.featureRowTitle}>
                {t('telegram_featured_paid_posts_title')}
              </div>
              <div className={s.featureRowCaption}>
                {t('telegram_featured_paid_posts_caption')}
              </div>
            </div>
            <div className={s.featureRow}>
              <div className={s.featureRowTitle}>{t('telegram_featured_sync_title')}</div>
              <div className={s.featureRowCaption}>
                {t('telegram_featured_sync_caption')}
              </div>
            </div>
            <div className={s.featureRow}>
              <div className={s.featureRowTitle}>
                {t('telegram_featured_broadcast_title')}
              </div>
              <div className={s.featureRowCaption}>
                {t('telegram_featured_broadcast_caption')}
              </div>
            </div>
          </div>
        </ScrollView>
      );
    }

    return (
      <ScrollView>
        <div className={s.actions}>
          {!connectedChannel && (
            <List
              title={t('telegram_channel_title')}
              description={t('telegram_channel_connect_caption')}
            >
              <ListItem chevron onClick={handleSelectChannel}>
                {t('telegram_channel_connect')}
              </ListItem>
            </List>
          )}
          {!!connectedChannel && (
            <List
              title={t('telegram_channel_title')}
              description={t('telegram_channel_connected_caption')}
            >
              <ListItem
                indicator={
                  connectedChannel.title.length > 24
                    ? connectedChannel.title.substring(0, 24) + '...'
                    : connectedChannel.title
                }
              >
                {t('telegram_channel_name')}
              </ListItem>
              <ListItem chevron onClick={handleSelectChannel}>
                {t('telegram_channel_change')}
              </ListItem>
              <ListItem
                description={inviteLink}
                icon={<IcCopy28 style={{ color: 'var(--tint)' }} />}
                onClick={handleCopyLink}
              >
                {t('telegram_channel_link')}
              </ListItem>
            </List>
          )}
          <List>
            <ListItem
              onClick={handleCreatePost}
              icon={<IcPlusCircle28 />}
              description={t('tg_bot_paid_post_desc')}
            >
              {t('tg_bot_paid_post_title')}
            </ListItem>
            <ListItem onClick={() => router.navigate(BOT)} icon={<IcSettings28 />}>
              {t('telegram_bot_settings')}
            </ListItem>
          </List>
        </div>
      </ScrollView>
    );
  }

  return (
    <>
      <Header showBackButton>Telegram</Header>
      {renderContent()}
    </>
  );
};
